import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import {
  Expense,
  ExpenseListResponse,
  ExpenseSummary,
  CreateExpenseRequest,
  SubmitExpenseRequest,
  ExpenseApprovalData,
  ExpenseFilters
} from '@/types/expense';

class ExpenseService {
  // Contractor endpoints
  
  /**
   * Create a new expense
   */
  async createExpense(data: CreateExpenseRequest): Promise<{ expense: Expense }> {
    return ApiService.request<{ expense: Expense }>(
      EndpointKeys.CONTRACTOR_EXPENSES_CREATE,
      {
        method: 'POST',
        body: data
      }
    );
  }

  /**
   * Submit an expense for approval
   */
  async submitExpense(expenseId: string, data: SubmitExpenseRequest): Promise<{ expense: Expense }> {
    return ApiService.request<{ expense: Expense }>(
      EndpointKeys.CONTRACTOR_EXPENSES_SUBMIT,
      {
        method: 'POST',
        body: data,
        urlParams: { id: expenseId }
      }
    );
  }

  /**
   * Get contractor's expenses with filtering and pagination
   */
  async getContractorExpenses(filters?: ExpenseFilters & {
    page?: number;
    pageSize?: number;
  }): Promise<ExpenseListResponse> {
    const queryParams: Record<string, string | number> = {};
    
    if (filters?.status?.length) {
      // For array parameters, we'll need to handle them in the URL construction
      filters.status.forEach((status, index) => {
        queryParams[`status_${index}`] = status;
      });
    }
    if (filters?.category?.length) {
      filters.category.forEach((category, index) => {
        queryParams[`category_${index}`] = category;
      });
    }
    if (filters?.contractId) {
      queryParams.contractId = filters.contractId;
    }
    if (filters?.startDate) {
      queryParams.startDate = filters.startDate;
    }
    if (filters?.endDate) {
      queryParams.endDate = filters.endDate;
    }
    if (filters?.minAmount !== undefined) {
      queryParams.minAmount = filters.minAmount;
    }
    if (filters?.maxAmount !== undefined) {
      queryParams.maxAmount = filters.maxAmount;
    }
    if (filters?.search) {
      queryParams.search = filters.search;
    }
    if (filters?.page) {
      queryParams.page = filters.page;
    }
    if (filters?.pageSize) {
      queryParams.pageSize = filters.pageSize;
    }

    return ApiService.request<ExpenseListResponse>(
      EndpointKeys.CONTRACTOR_EXPENSES_LIST,
      {
        queryParams
      }
    );
  }

  /**
   * Get expense details by ID
   */
  async getExpenseDetails(expenseId: string): Promise<{ expense: Expense }> {
    return ApiService.request<{ expense: Expense }>(
      EndpointKeys.CONTRACTOR_EXPENSES_DETAILS,
      {
        urlParams: { id: expenseId }
      }
    );
  }

  /**
   * Get expense summary for contractor dashboard
   */
  async getExpenseSummary(): Promise<ExpenseSummary> {
    const response = await ApiService.request<{ summary: ExpenseSummary }>(
      EndpointKeys.CONTRACTOR_EXPENSES_SUMMARY
    );
    return response.summary;
  }

  /**
   * Get expense summary for a specific contract (for clients)
   */
  async getContractExpenseSummary(contractId: string): Promise<ExpenseSummary> {
    const response = await ApiService.request<{ summary: ExpenseSummary }>(
      EndpointKeys.CONTRACTOR_CONTRACT_EXPENSES_SUMMARY,
      {
        urlParams: { id: contractId }
      }
    );
    return response.summary;
  }

  // Client endpoints

  /**
   * Get expenses pending approval for a specific contract
   */
  async getExpensesForClientReview(contractId: string): Promise<ExpenseListResponse> {
    return ApiService.request<ExpenseListResponse>(
      EndpointKeys.CONTRACTOR_CONTRACT_EXPENSES_PENDING_APPROVAL,
      {
        urlParams: { id: contractId }
      }
    );
  }

  /**
   * Approve or reject an expense
   */
  async approveOrRejectExpense(
    expenseId: string, 
    data: ExpenseApprovalData
  ): Promise<{ expense: Expense }> {
    return ApiService.request<{ expense: Expense }>(
      EndpointKeys.CONTRACTOR_EXPENSES_APPROVE_REJECT,
      {
        method: 'POST',
        body: data,
        urlParams: { id: expenseId }
      }
    );
  }

  /**
   * Get approved expenses for invoicing
   */
  async getApprovedExpensesForInvoicing(contractId: string): Promise<{ expenses: Expense[] }> {
    return ApiService.request<{ expenses: Expense[] }>(
      EndpointKeys.CONTRACTOR_CONTRACT_EXPENSES_APPROVED,
      {
        urlParams: { id: contractId }
      }
    );
  }

  /**
   * Mark expense as invoiced
   */
  async markExpenseAsInvoiced(expenseId: string, invoiceLineItemId: number): Promise<{ expense: Expense }> {
    return ApiService.request<{ expense: Expense }>(
      EndpointKeys.CONTRACTOR_EXPENSES_MARK_INVOICED,
      {
        method: 'POST',
        body: { invoiceLineItemId },
        urlParams: { id: expenseId }
      }
    );
  }

  /**
   * Get a presigned URL for downloading an expense receipt
   */
  async getReceiptDownloadUrl(expenseId: string): Promise<{ presignedUrl: string | null }> {
    return ApiService.request<{ presignedUrl: string | null }>(
      EndpointKeys.CONTRACTOR_EXPENSE_RECEIPT_DOWNLOAD_URL,
      {
        queryParams: { expense_id: expenseId }
      }
    );
  }

  /**
   * Download an expense receipt (opens in new tab)
   */
  async downloadReceipt(expenseId: string): Promise<void> {
    try {
      const response = await this.getReceiptDownloadUrl(expenseId);
      if (response.presignedUrl) {
        window.open(response.presignedUrl, '_blank');
      } else {
        throw new Error('No receipt available for this expense');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      throw error;
    }
  }

  // Utility methods

  /**
   * Format expense amount for display
   * @param amount - Amount to format
   * @param currencyCode - Optional currency code (defaults to GBP for backward compatibility)
   * @param locale - Optional locale (defaults to en-GB)
   */
  formatAmount(amount: string | number, currencyCode: string = 'GBP', locale: string = 'en-GB'): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Handle USD with US locale for proper formatting
    if (currencyCode === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    }
    
    // Default to GBP with GB locale
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }

  /**
   * Format expense date for display
   * @param dateString - Date string to format
   * @param countryCode - Optional country code for locale-specific formatting (defaults to GB)
   */
  formatDate(dateString: string, countryCode: string = 'GB'): string {
    // Use appropriate locale based on country
    const locale = countryCode === 'US' ? 'en-US' : 'en-GB';
    
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(dateString));
  }

  /**
   * Format expense datetime for display
   * @param dateString - DateTime string to format
   * @param countryCode - Optional country code for locale-specific formatting (defaults to GB)
   */
  formatDateTime(dateString: string, countryCode: string = 'GB'): string {
    // Use appropriate locale based on country
    const locale = countryCode === 'US' ? 'en-US' : 'en-GB';
    
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  }

  /**
   * Validate expense form data
   */
  validateExpenseForm(data: Partial<CreateExpenseRequest>): {
    isValid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    if (!data.contractorContractId) {
      errors.contractorContractId = 'Contract is required';
    }

    if (!data.amount) {
      errors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(data.amount);
      if (isNaN(amount) || amount <= 0) {
        errors.amount = 'Amount must be a positive number';
      }
    }

    if (!data.description?.trim()) {
      errors.description = 'Description is required';
    }

    if (!data.expenseDate) {
      errors.expenseDate = 'Expense date is required';
    } else {
      const expenseDate = new Date(data.expenseDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (expenseDate > today) {
        errors.expenseDate = 'Expense date cannot be in the future';
      }
    }

    if (!data.category) {
      errors.category = 'Category is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new ExpenseService(); 